<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { getContext, type Component, type Snippet } from "svelte";
	import { readable, type Readable, type Writable } from "svelte/store";
	import { twMerge } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import { LandingPageSchema, RenderMode, type AssetRef, type LandingPage, type Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import type { EntryAsset } from "#lib/assetManager.js";
	import AppHeader from "#lib/components/flow/viewer/AppHeader.svelte";
	import AppSwitcher from "#lib/components/flow/viewer/AppSwitcher.svelte";
	import AppSwitcherLayout from "#lib/components/flow/viewer/AppSwitcherLayout.svelte";
	import FooterLinks from "#lib/components/flow/viewer/FooterLinks.svelte";
	import Module from "#lib/components/flow/viewer/Module.svelte";
	import DesktopAppSwitcherLayout from "#lib/components/flow/viewer/desktop/AppSwitcherLayout.svelte";
	import Classic from "#lib/components/flow/viewer/layout/Classic.svelte";
	import Legacy from "#lib/components/flow/viewer/layout/Legacy.svelte";
	import Modern from "#lib/components/flow/viewer/layout/Modern.svelte";
	import Profile from "#lib/components/flow/viewer/layout/Profile.svelte";
	import LegacyDesktop from "#lib/components/flow/viewer/layout/desktop/Legacy.svelte";
	import type { LayoutProps } from "#lib/components/flow/viewer/layout/types.js";
	import type { AppSwitcherLayoutProps, ContainerProps } from "#lib/components/flow/viewer/types.js";
	import { isPassthroughType, resolveBackgroundPaint, resolvedModuleNode } from "#lib/components/flow/viewer/utils.js";
	import { APP_ANCHOR_NAME, CONVERSION_ANCHOR_NAME, SECTION_1_ANCHOR_NAME, SECTION_2_ANCHOR_NAME, type BundleNode } from "#lib/dag.js";
	import { createResolutionsStore, type ResolutionsStore } from "#lib/passthrough.js";
	import { createInterpolatedLandingPageStore } from "#lib/stores/asset.js";
	import { getBackgroundColor, isLight, styleWithBrandKit } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";

	interface Factory {
		mobile: Component<LayoutProps<LandingPage>>;
		desktop: Component<LayoutProps<LandingPage>>;
		hasDesktopLayout: boolean;
	}

	const factory: Record<string, Factory> = {
		classic: { mobile: Classic, desktop: Classic, hasDesktopLayout: false },
		modern: { mobile: Modern, desktop: Modern, hasDesktopLayout: false },
		profile: { mobile: Profile, desktop: Profile, hasDesktopLayout: false },
		legacy: { mobile: Legacy, desktop: LegacyDesktop, hasDesktopLayout: true },
	};

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const suite: Writable<Suite> = getContext("suite");
	const entryAsset: Writable<EntryAsset | undefined> = getContext("entryAsset");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");
	// The isDesktop prop is already ANDed with desktopViewEnabled, so read the raw signal here.
	const isDesktopUserAgent: Readable<boolean | undefined> = getContext<Readable<boolean | undefined> | undefined>("isDesktop") ?? readable(false);
	const suiteForPaint: Readable<Suite | undefined> = getContext<Readable<Suite | undefined> | undefined>("suite") ?? readable(undefined);
	const passthroughResolutions: ResolutionsStore = getContext<ResolutionsStore | undefined>("passthroughResolutions") ?? createResolutionsStore();

	const {
		dag,
		asset,
		mode = Mode.LIVE,
		readOnly = false,
		selected = false,
		interpolate = false,
		isDesktop = false,
		modal = false,
		canConvert = true,
		showDisabled = false,
		metadata,
		onExecute,
		onCancel,
		onShow,
		onEdit,
		onMore,
		onShare,
		onMenu,
		section1: nestedSection1,
		section2: nestedSection2,
		apps: nestedApps,
		conversion: nestedConversion,
		app: appAccessory,
		...other
	}: ContainerProps = $props();

	const key = $derived(asset?.key());
	const landingPage = $derived(mode === Mode.EDITING && interpolate ? createInterpolatedLandingPageStore(tokenProvider, suite, entryAsset, asset?.asLandingPage() ?? readable(create(LandingPageSchema)), mode) : (asset?.asLandingPage() ?? readable(create(LandingPageSchema))));
	const section1Modules = $derived(dag.findModulesFromAssetKey(key, SECTION_1_ANCHOR_NAME));
	const section2Modules = $derived(dag.findModulesFromAssetKey(key, SECTION_2_ANCHOR_NAME));
	const conversionModules = $derived(dag.findModulesFromAssetKey(key, CONVERSION_ANCHOR_NAME));
	const appModules = $derived(dag.findModulesFromAssetKey(key, APP_ANCHOR_NAME));
	const accessory = $derived(getAccessory(nestedSection1, nestedSection2, nestedApps, nestedConversion));
	const additional = $derived(appModules.filter(isModuleEnabled));
	const hasPrimaryConversion = $derived(conversionModules.filter(isModuleEnabled).length > 0);
	const promotedApp = $derived(additional[0] && isPassthroughType(additional[0].ref?.type) ? resolvedModuleNode(dag, additional[0], $passthroughResolutions.map[additional[0].key]) : additional[0]);
	const shouldPromoteFirstApp = $derived(isDesktop && (!$landingPage.layoutName || $landingPage.layoutName === "legacy") && !hasPrimaryConversion && additional.length > 0 && promotedApp?.ref?.renderMode === RenderMode.INLINE);
	const themeStyle = $derived(styleWithBrandKit($brandKit, $landingPage));
	const backgroundPaint = $derived(resolveBackgroundPaint({ mode, modal, isDesktopUserAgent: $isDesktopUserAgent, desktopViewEnabled: $suiteForPaint?.desktopViewEnabled }));
	const backgroundColor = $derived(getBackgroundColor($brandKit, $landingPage));
	const isLightBackground = $derived(isLight(backgroundColor));
	const inlineClass = "bg-transparent!";
	const outlineClass = $derived(mode === Mode.EDITING && selected ? "outline outline-offset-4 outline-transparent hover:outline-primary-600" : "");
	const readOnlyClass = $derived(readOnly ? "disabled:opacity-100" : "");
	const borderClass = $derived(isLightBackground ? "border border-gray-300" : "border border-[var(--background-color)]");
	const buttonClass = $derived(isDesktop && isLightBackground ? "border-gray-900" : "border-[var(--primary-button-background-color)]");
	const Layout = $derived(getLayout($landingPage.layoutName, isDesktop));
	const Switcher = $derived(getAppSwitcherLayout($landingPage.layoutName, isDesktop));

	function getLayout(name: string | undefined, isDesktop?: boolean): Component<LayoutProps<LandingPage>> {
		const entry = name ? factory[name] : undefined;
		if (!entry) {
			return isDesktop ? LegacyDesktop : Legacy;
		}
		return entry[isDesktop ? "desktop" : "mobile"] || (isDesktop ? LegacyDesktop : Legacy);
	}

	function getAppSwitcherLayout(name: string | undefined, isDesktop?: boolean): Component<AppSwitcherLayoutProps> {
		const entry = name ? factory[name] : undefined;
		if (!entry) {
			return isDesktop ? DesktopAppSwitcherLayout : AppSwitcherLayout;
		}
		return isDesktop && entry["desktop"] !== entry["mobile"] ? DesktopAppSwitcherLayout : AppSwitcherLayout;
	}

	function getAccessory(section1?: Snippet, section2?: Snippet, apps?: Snippet, conversion?: Snippet): Record<string, Snippet | undefined> {
		return {
			[SECTION_1_ANCHOR_NAME]: section1,
			[SECTION_2_ANCHOR_NAME]: section2,
			[APP_ANCHOR_NAME]: apps,
			[CONVERSION_ANCHOR_NAME]: conversion,
		};
	}

	function isModuleEnabled(node: BundleNode<AssetRef>): boolean {
		return !node?.ref.disabled || showDisabled;
	}
</script>

<Layout {...other} {dag} {key} container={$landingPage} {mode} {readOnly} {modal} {backgroundPaint} numApps={additional?.length ?? 0} {hasPrimaryConversion} {shouldPromoteFirstApp} {accessory} {onShow} {onMore} {onMenu} {onShare} {isLightBackground} style={themeStyle}>
	{#snippet share()}
		<AppHeader {landingPage} {readOnly} {onShare} {onMenu} />
	{/snippet}
	{#snippet section1()}
		{#each section1Modules.filter(isModuleEnabled) as module (module.key)}
			<div class={twMerge(outlineClass, "rounded-xl")}>
				<Module {module} {metadata} {mode} {themeStyle} {isLightBackground} {readOnly} {interpolate} layoutName={$landingPage.layoutName} {onShow} {onExecute} {onCancel} {onEdit} classes={{ inlineClass, borderClass, readOnlyClass, outlineClass }} {isDesktop} />
			</div>
		{/each}
	{/snippet}
	{#snippet conversion()}
		{#if shouldPromoteFirstApp}
			<AppSwitcher layoutName={$landingPage.layoutName} page={additional[0]} {mode} {themeStyle} {isLightBackground} {readOnly} {interpolate} {metadata} {onExecute} {onCancel} {onShow} classes={{ readOnlyClass, outlineClass, borderClass }} />
		{:else}
			{#each conversionModules.filter(isModuleEnabled) as module (module.key)}
				<div class={twMerge(outlineClass, "rounded-xl")}>
					<Module {module} {metadata} {mode} {themeStyle} {isLightBackground} {readOnly} {interpolate} layoutName={$landingPage.layoutName} actionEnabled={canConvert} {onShow} {onExecute} {onCancel} {onEdit} classes={{ buttonClass, outlineClass, borderClass, readOnlyClass }} isConversionModule={true} {isDesktop} />
				</div>
			{/each}
		{/if}
	{/snippet}
	{#snippet section2()}
		{#each section2Modules.filter(isModuleEnabled) as module (module.key)}
			<div class={twMerge(outlineClass, "rounded-xl")}>
				<Module {module} {metadata} {mode} {themeStyle} {isLightBackground} {readOnly} {interpolate} layoutName={$landingPage.layoutName} {onShow} {onExecute} {onCancel} {onEdit} classes={{ inlineClass, borderClass, readOnlyClass, outlineClass }} {isDesktop} />
			</div>
		{/each}
	{/snippet}
	{#snippet apps()}
		{#if additional.length > 0}
			{@const resolvedApps = shouldPromoteFirstApp ? additional.slice(1, additional.length) : additional}
			<Switcher apps={resolvedApps} {metadata} {mode} {themeStyle} {isLightBackground} {readOnly} {interpolate} layoutName={$landingPage.layoutName} maxVisibleModules={$landingPage.maxVisibleModules} accessory={appAccessory} {onShow} {onMore} {onExecute} {onCancel} {onEdit} classes={{ readOnlyClass, outlineClass, borderClass }} />
		{/if}
	{/snippet}
	{#snippet footer(mode)}
		<FooterLinks {backgroundColor} {mode} />
	{/snippet}
</Layout>
